import { useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import { api, ApiError } from '../lib/api';
import type { CompanyKind, CompanyStatus } from '../lib/types';

const STATUSES: Array<{ value: CompanyStatus; label: string; hint: string }> = [
  { value: 'active', label: 'Active', hint: 'Work is happening.' },
  { value: 'prospect', label: 'Prospect', hint: 'Not a customer yet.' },
  { value: 'dormant', label: 'Dormant', hint: 'Nothing running, but they may come back.' },
  { value: 'archived', label: 'Archived', hint: 'Finished. Kept for the record.' },
];

/**
 * Correcting a company's details.
 *
 * The API has always been able to do this — `PATCH /companies/:id`, gated on `company.manage`.
 * Nothing in the app ever called it, so a name typed wrong, or a contact who has since left,
 * could be entered once and never fixed. That is the gap this closes; the endpoint needed
 * nothing.
 *
 * THE CODE IS NOT HERE, and that is deliberate rather than an omission. It prefixes every work
 * item key ever issued — GOLD-PORTAL-17 — so changing it would either orphan those keys or
 * rewrite history. `companies.service` refuses it for the same reason; the form says so rather
 * than leaving somebody hunting for the field.
 */
interface CompanyDetail {
  id: string;
  name: string;
  code: string;
  kind: CompanyKind;
  status: CompanyStatus;
  notes: string;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
}

export function EditCompanyModal({
  companyId,
  onClose,
  onSaved,
}: {
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  /**
   * Fetched when the form opens rather than passed in.
   *
   * The company dashboard carries only id, name, code and kind — enough for a heading, not
   * enough to edit. Widening that payload for a form most people never open would make every
   * page load carry the notes and the contact; one request when the form opens is the cheaper
   * trade, and it means the fields show what is actually stored right now.
   */
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CompanyKind>('external');
  const [status, setStatus] = useState<CompanyStatus>('active');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void api
      .get<{ company: CompanyDetail }>(`/companies/${companyId}`)
      .then(({ company: row }) => {
        if (!live) return;
        setCompany(row);
        setName(row.name);
        setKind(row.kind);
        setStatus(row.status);
        setContactName(row.primaryContactName ?? '');
        setContactEmail(row.primaryContactEmail ?? '');
        setNotes(row.notes ?? '');
      })
      .catch((err) => live && setError(err instanceof ApiError ? err.message : 'Could not load this company'));
    return () => {
      live = false;
    };
  }, [companyId]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/companies/${companyId}`, {
        name: name.trim(),
        kind,
        status,
        primaryContactName: contactName.trim(),
        primaryContactEmail: contactEmail.trim(),
        notes: notes.trim(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save those details');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={company ? `Edit ${company.name}` : "Edit company"}
      subtitle="Who we call, when they work, and what they are to us."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-company" className="btn btn-primary" disabled={busy || !company || !name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}

      {!company ? (
        <p className="dim">Loading…</p>
      ) : (
      <form id="edit-company" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">Company name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>

        <div className="field">
          <span className="field-label">Short code</span>
          <input type="text" value={company?.code ?? ""} disabled className="mono" />
          <span className="field-hint">
            Permanent. It prefixes every work item ever raised here — {company?.code ?? "CODE"}-PORTAL-17 — so changing
            it would break keys that have already been sent to people.
          </span>
        </div>

        <label className="field">
          <span className="field-label">Type</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as CompanyKind)}>
            <option value="internal">Internal — part of Egen Holdings</option>
            <option value="external">External — a customer</option>
          </select>
        </label>

        <label className="field">
          <span className="field-label">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as CompanyStatus)}>
            {STATUSES.map((row) => (
              <option key={row.value} value={row.value}>
                {row.label}
              </option>
            ))}
          </select>
          <span className="field-hint">{STATUSES.find((row) => row.value === status)?.hint}</span>
        </label>

        <label className="field">
          <span className="field-label">Primary contact</span>
          <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} />
          <span className="field-hint">Who we call. Leave empty if there is nobody named yet.</span>
        </label>

        <label className="field">
          <span className="field-label">Contact email</span>
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          <span className="field-hint">
            Anything the contact fields cannot hold — a switchboard number, a second person to try.
          </span>
        </label>
      </form>
      )}
    </Modal>
  );
}
